import React from 'react';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { LinearProgress, Typography, useTheme } from '@mui/material';
import ReactApexChart from 'react-apexcharts';

import { useSettings, Utils, Icon } from 'app/main/components';
import { DetailsSectionWrapper } from 'app/main/components/page-layouts';
import type { MiddlewhereFilter } from 'app/main/components/filter/FilterItemManager';

import { PhonceCallsChartYearSelector } from './PhoneCallsChartsUtils';

export interface PhoneCallsChartProps {
	refresh: boolean;
	filters: MiddlewhereFilter[];
}

export default function PhoneCallsChart(props: PhoneCallsChartProps) {
	const dispatch = useDispatch();
	const { t } = useSettings();
	const theme = useTheme();

	const currentYear = Utils.today().year();

	const [isLoading, setIsLoading] = React.useState<boolean>(false);
	const [year, setYear] = React.useState<number>(currentYear);

	const [results, setResults] = React.useState<{
		incomingDuration: number[];
		outgoingDuration: number[];
		incomingCount: number[];
		outgoingCount: number[];
	}>();

	const [totals, setTotals] = React.useState<{
		incomingDuration: number;
		outgoingDuration: number;
	}>();

	React.useEffect(() => {
		setIsLoading(true);
		Utils.executeStored({
			action: 'st_sys_statstable_list',
			data: {
				view: 'v_phonecalls_stats',
				field: 'phca_date_monthyear',
				columns: ['phca_duration_in', 'phca_duration_out', 'phca_count_in', 'phca_count_out'],
				n_page: 1,
				n_rows: 30,
				filters: [
					...props.filters,
					{
						filt_field: 'phca_date',
						filt_clause: 'range',
						filt_value: { from: moment.utc([year]).startOf('year'), to: moment.utc([year]).endOf('year') }
					}
				]
			},
			dispatch,
			successHandler: response => {
				if (response.data?.n_record > 0) {
					const incomingDuration = response.data.list.map(item => item.phca_duration_in);
					const outgoingDuration = response.data.list.map(item => item.phca_duration_out);
					const incomingCount = response.data.list.map(item => item.phca_count_in);
					const outgoingCount = response.data.list.map(item => item.phca_count_out);

					setResults({
						incomingDuration,
						outgoingDuration,
						incomingCount,
						outgoingCount
					});
					setTotals({
						incomingDuration: response.data.totals?.phca_duration_in,
						outgoingDuration: response.data.totals?.phca_duration_out
					});
				} else {
					setResults(undefined);
					setTotals(undefined);
				}

				setIsLoading(false);
			}
		});
	}, [dispatch, props.filters, props.refresh, year]);

	const chartOptions: ApexCharts.ApexOptions = {
		chart: {
			width: '100%',
			height: 320,
			fontFamily: 'inherit',
			foreColor: 'inherit',
			toolbar: { show: false },
			sparkline: { enabled: true }
		},
		stroke: {
			curve: 'smooth',
			width: 2
		},
		markers: { size: 2 },
		grid: { show: false, padding: { top: 4, bottom: 4, left: 4, right: 4 } },
		plotOptions: {
			bar: {
				columnWidth: 10,
				borderRadius: 4,
				borderRadiusApplication: 'end',
				horizontal: false
			}
		},
		dataLabels: { enabled: false },
		xaxis: {
			labels: { show: false },
			axisBorder: { show: true },
			axisTicks: { show: false },
			categories: moment.months().map(month => `${month} ${year}`)
		},
		yaxis: [
			{
				seriesName: t('PHONECALLS_DIRECTION_INCOMING'),
				labels: {
					show: false,
					formatter: value => Utils.formatDuration({ seconds: value })
				},
				axisBorder: { show: false },
				axisTicks: { show: false }
			},
			{
				seriesName: t('PHONECALLS_DIRECTION_INCOMING'),
				labels: {
					show: false,
					formatter: value => Utils.formatDuration({ seconds: value })
				},
				show: false
			},
			{
				seriesName: t('PHONECALLS_COUNT_IN'),
				labels: { show: false },
				axisBorder: { show: false },
				axisTicks: { show: false }
			},
			{
				seriesName: t('PHONECALLS_COUNT_IN'),
				show: false
			}
		],
		tooltip: {
			followCursor: true,
			shared: true,
			intersect: false
		}
	};

	const series: ApexAxisChartSeries = [
		{
			name: t('PHONECALLS_DIRECTION_INCOMING'),
			type: 'column',
			color: theme.palette.success.dark,
			data: results?.incomingDuration || []
		},
		{
			name: t('PHONECALLS_DIRECTION_OUTGOING'),
			type: 'column',
			color: theme.palette.success.light,
			data: results?.outgoingDuration || []
		},
		{
			name: t('PHONECALLS_COUNT_IN'),
			type: 'line',
			data: results?.incomingCount || []
		},
		{
			name: t('PHONECALLS_COUNT_OUT'),
			type: 'line',
			data: results?.outgoingCount || []
		}
	];

	return (
		<DetailsSectionWrapper
			title={t('PHONECALLS_CHART_TITLE')}
			subheader={
				<>
					<Typography variant="subtitle2">
						<FormattedMessage
							id="phoneCallsChart.totalIncomingDuration"
							defaultMessage={t('PHONECALLS_CHART_TOTAL_PHCA_DURATION_IN')}
							values={{ value: Utils.formatDuration({ seconds: totals?.incomingDuration }) }}
						/>
					</Typography>
					<Typography variant="subtitle2">
						<FormattedMessage
							id="phoneCallsChart.totalOutgoingDuration"
							defaultMessage={t('PHONECALLS_CHART_TOTAL_PHCA_DURATION_OUT')}
							values={{ value: Utils.formatDuration({ seconds: totals?.outgoingDuration }) }}
						/>
					</Typography>
				</>
			}
			avatar={<Icon icon="chart-bar" />}
			headerAction={<PhonceCallsChartYearSelector year={year} setYear={setYear} />}
			disablePadding
		>
			{isLoading ? (
				<LinearProgress variant="query" />
			) : (
				<ReactApexChart type="bar" options={chartOptions} series={series} />
			)}
		</DetailsSectionWrapper>
	);
}
